// 마을 RPG 창(가방·상점·대장간·교관)의 그림·등급 데이터(표시 전용). 게임 규칙·가격·스탯은 shared/rpg가 정본이다.
// 정본 설계도: work/blueprint(노드 ui-bag-*, ui-npc-*), 콘셉트: concept/inventory-2026-10-17.
import type { GearTier, ItemId, ItemSlot, NpcRole } from '../shared/rpg';
import { assetUrl } from '../pwa/assetMap';

export type Rarity = 'common' | 'uncommon' | 'rare' | 'legend';
export interface RarityStyle { readonly label: string; readonly color: string; readonly bg1: string; readonly bg2: string }
export const RARITY: Record<Rarity, RarityStyle> = {
  common: { label: '일반', color: '#c3cad6', bg1: '#4a5262', bg2: '#1c2130' },
  uncommon: { label: '고급', color: '#62dd8f', bg1: '#2c6a46', bg2: '#10261c' },
  rare: { label: '희귀', color: '#5cb2ff', bg1: '#2b5aa0', bg2: '#0f1d3c' },
  legend: { label: '전설', color: '#ffa94d', bg1: '#8a3d1a', bg2: '#2a100a' },
};
export const ITEM_RARITY: Record<ItemId, Rarity> = {
  sword_worn: 'common', sword_training: 'uncommon', sword_steel: 'rare', sword_ember: 'legend', armor_travel: 'uncommon', armor_chain: 'rare', armor_crimson: 'legend',
};
export const SLOT_LABEL: Record<ItemSlot, string> = { weapon: '무기', armor: '갑옷' };

export function rarityOf(id: string): RarityStyle {
  return RARITY[ITEM_RARITY[id as ItemId] ?? 'common'];
}
/** 타일·카드에 붙이는 등급 CSS 변수 */
export function rarityVars(id: string): string {
  const r = rarityOf(id);
  return '--rc:' + r.color + ';--rbg1:' + r.bg1 + ';--rbg2:' + r.bg2 + ';';
}

export function itemIconUrl(id: string): string { return assetUrl('ui/items/' + id + '.webp'); }
/**
 * 보석상·연금술사(S7)는 전용 그림이 없어 3D와 같은 모델의 그림을 옷 색만 바꿔 쓴다(env/npcs.ts 색 곱과 같은 결):
 * 보석상 = 의뢰인(교관) 그림 + 푸른빛, 연금술사 = 잡화상 그림 + 초록빛. NPC_ART_FILTER가 그 CSS 필터다
 */
function artRole(role: NpcRole): 'shop' | 'forge' | 'trainer' | 'raid' { return role === 'jeweler' ? 'trainer' : role === 'alchemist' ? 'shop' : role; }
/** 레이드 지휘관(RAID-PRD 9절)은 전용 그림(ui/npc/raid.webp·raid_cut.webp, Codex CLI 이미지)이라 필터가 없다 */
export const NPC_ART_FILTER: Readonly<Record<NpcRole, string>> = {
  shop: '', forge: '', trainer: '', jeweler: 'hue-rotate(165deg) saturate(1.15)', alchemist: 'hue-rotate(38deg) saturate(1.05)', raid: '',
};
/** NPC 초상. art = 마을 주민 전용 그림 id(shared/townStory, TOWN-SCENARIO T4)가 있으면 그 그림 */
export function npcPortraitUrl(role: NpcRole, art?: string): string { return assetUrl('ui/npc/' + (art ?? artRole(role)) + '.webp'); }
/** 가로 화면 왼쪽에 크게 세우는 NPC 상반신(투명 배경). art는 위와 같다 */
export function npcCutoutUrl(role: NpcRole, art?: string): string { return assetUrl('ui/npc/' + (art ?? artRole(role)) + '_cut.webp'); }
/** 가방 전신 그림. 장착 갑옷 단계와 같은 차림(T1은 기존 승인 그림) */
export function kaelStandeeUrl(tier: GearTier = 1): string { return assetUrl('ui/' + (tier === 1 ? 'kael_standee.webp' : 'kael_standee_t' + tier + '.webp')); }

/** 능력치 아이콘(인라인 SVG, currentColor) */
export const STAT_ICON: Record<'attack' | 'maxHp' | 'maxMp' | 'critChance' | 'cooldown', string> = {
  attack: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M20.5 2.5 13 4l-8 8 2 2-3.5 3.5 1 1L2 21l1 1 2.5-2.5 1 1L10 17l2 2 8-8 1.5-7.5zM12.7 16.3l-5-5 6.6-6.6 4.4-.9-.9 4.4z"/></svg>',
  maxHp: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 21s-8.5-5.4-8.5-11.3A4.7 4.7 0 0 1 12 7a4.7 4.7 0 0 1 8.5 2.7C20.5 15.6 12 21 12 21z"/></svg>',
  maxMp: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2.2s7 7.7 7 12.6a7 7 0 0 1-14 0C5 9.9 12 2.2 12 2.2z"/></svg>',
  critChance: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="m12 1.5 2.6 7.3 7.4.2-5.9 4.6 2.2 7.4L12 16.6 5.7 21l2.2-7.4L2 9l7.4-.2z"/></svg>',
  cooldown: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6 2h12v2h-1.5v2.6a5 5 0 0 1-2.2 4.1L13 11.9v.2l1.3 1.2a5 5 0 0 1 2.2 4.1V20H18v2H6v-2h1.5v-2.6a5 5 0 0 1 2.2-4.1l1.3-1.2v-.2l-1.3-1.2A5 5 0 0 1 7.5 6.6V4H6zm3.5 2v2.6c0 1 .5 1.9 1.3 2.5l1.2.9 1.2-.9c.8-.6 1.3-1.5 1.3-2.5V4z"/></svg>',
};

